import { updateAddress, updatePhone } from '@/api/customer-api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { Customer } from '@/types/customer-type';
import { OrderStatus } from '@/types/order-type';
import { Typography } from '@mui/material';
import { AlertCircle, Clock, EditIcon, Eye, MapPin, MessageCircle, Phone, Plus } from 'lucide-react';
import { useState } from 'react';
import { Label } from 'recharts';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Facebook } from '@mui/icons-material';
import BlockedCustomerBanner from '../customers/BlockedCustomerBanner';
import { formatTimeLabel } from '@/lib/utils';

interface CustomerInfoCardProps {
  customer: Customer;
  onViewProfile?: () => void;
  onRefresh?: () => void;
  orderId: string;
  shippingAddress: string | null;
  orderStatus?: OrderStatus;
}

export default function CustomerInfoCard({
  customer,
  onViewProfile,
  onRefresh,
  orderId,
  shippingAddress,
  orderStatus,
}: CustomerInfoCardProps) {
  const [open, setOpen] = useState(false);
  const [address, setAddress] = useState(shippingAddress ?? '');
  const [saving, setSaving] = useState(false);
  const [openPhone, setOpenPhone] = useState(false);
  const [phone, setPhone] = useState(customer.phone ?? '');
  const [savingPhone, setSavingPhone] = useState(false);
  const canUpdateOrderAddress =
    orderStatus == OrderStatus.CONFIRMED || orderStatus == OrderStatus.DRAFT;

  async function handleSavePhone() {
    setSavingPhone(true);
    const resp = await updatePhone(customer.id, phone.trim());
    if (resp.success) {
      toast({
        title: 'Success',
        description: 'Customer address has been updated successfully',
      });
      setOpen(false);
      if (onRefresh) {
        onRefresh();
      }
    }
    setSavingPhone(false);
    setOpenPhone(false);
  }
  const handleSave = async () => {
    try {
      setSaving(true);
      const resp = await updateAddress(customer.id, orderId, address.trim());
      if (resp.success) {
        toast({
          title: 'Success',
          description: 'Customer address has been updated successfully',
        });
        setOpen(false);
        if (onRefresh) {
          onRefresh();
        }
      }
    } catch (error) {
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border border-slate-200 shadow-sm rounded-xl">
      <CardHeader className="flex flex-row items-center justify-between">
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            lineHeight: 1.2,
            fontSize: { xs: '0.9rem', sm: '1rem' },
            color: 'text.primary',
          }}
        >
          Customer Info
        </Typography>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1 text-slate-600 hover:text-slate-900"
          onClick={onViewProfile}
        >
          <Eye className="h-4 w-4" />
          View
        </Button>
      </CardHeader>

      <CardContent className="space-y-4">
        <BlockedCustomerBanner customer={customer} />
        {/* Customer Basic */}
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 bg-white">
            <div className="flex gap-3 min-w-0">
              <div className="relative flex-shrink-0">
                {customer?.profileImageUrl ? (
                  <img
                    className="w-10 h-10 rounded-full object-cover"
                    src={customer.profileImageUrl!}
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-medium">
                    {customer?.name?.charAt(0)}
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                  {customer.name}
                </h3>
                <p className="text-xs sm:text-sm text-green-600">online</p>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-start space-x-3">
          <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
            <MapPin className="w-4 h-4 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900">Shipping Address</p>
            <div className="flex items-center justify-between w-full">
              <p className="text-sm text-gray-600 truncate max-w-[80%]">
                {shippingAddress || 'Not provided'}
              </p>

              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button type="button" variant="ghost" size="xs" title="Add Customer Address">
                    {!customer.address ? (
                      <Plus className="h-3 w-3" />
                    ) : (
                      <EditIcon className="h-3 w-3" />
                    )}
                    {!customer.address ? 'Add' : 'Edit'}
                  </Button>
                </DialogTrigger>

                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Edit Shipping Address</DialogTitle>
                  </DialogHeader>
                  {!canUpdateOrderAddress && (
                    <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-800 border border-amber-200">
                      <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                      <div>
                        <p className="font-semibold">Notice</p>
                        <p>
                          Updates to your address will not affect this shipment because the order
                          has already passed the <strong>{orderStatus}</strong> stage.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="space-y-3 py-2">
                    <div className="space-y-1">
                      <Label>Address</Label>
                      <Textarea
                        id="shipping-address"
                        rows={4}
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="Street, city, province, postal code"
                        className="text-sm"
                      />
                    </div>
                  </div>

                  <DialogFooter className="gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setOpen(false)}
                    >
                      Cancel
                    </Button>

                    <Button type="button" size="sm" onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
        <div className="flex items-start space-x-3">
          <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
            <Phone className="w-4 h-4 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900">Phone</p>
            <div className="flex items-center justify-between w-full">
              <p className="text-sm text-gray-600 truncate max-w-[80%]">
                {customer.phone || 'Not provided'}
              </p>

              {/* Add / Edit Phone button + dialog */}
              <Dialog open={openPhone} onOpenChange={setOpenPhone}>
                <DialogTrigger asChild>
                  <Button type="button" variant="ghost" size="xs">
                    {!customer.phone ? (
                      <Plus className="h-3 w-3" />
                    ) : (
                      <EditIcon className="h-3 w-3" />
                    )}
                    {!customer.phone ? 'Add' : 'Edit'}
                  </Button>
                </DialogTrigger>

                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Add Customer Phone</DialogTitle>
                  </DialogHeader>

                  <div className="space-y-3 py-2">
                    <div className="space-y-1">
                      <Label>Phone</Label>
                      <Input
                        id="customer-phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="012 345 678"
                        className="text-sm"
                      />
                    </div>
                  </div>

                  <DialogFooter className="gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setOpenPhone(false)}
                    >
                      Cancel
                    </Button>

                    <Button
                      type="button"
                      size="sm"
                      onClick={handleSavePhone}
                      disabled={savingPhone}
                    >
                      {savingPhone ? 'Saving...' : 'Save'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
        {/* <div className="flex items-start space-x-3">
          <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
            <Facebook className="w-4 h-4 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900">FB Interaction</p>
            <div className="mt-2 text-sm text-gray-600 w-full space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 w-24 shrink-0">Facebook ID:</span>
                <span className="truncate font-mono text-xs">{customer.facebookId || 'NA'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 w-24 shrink-0">Conversation ID:</span>
                <span className="truncate font-mono text-xs">{customer.facebookConversationId || 'NA'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs text-gray-500 w-24 shrink-0">
                  <MessageCircle className="h-3 w-3" /> Chat:
                </span>
                <span className="truncate text-xs">
                  {customer.lastFacebookMessage ? formatTimeLabel(customer.lastFacebookMessage) : 'NA'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs text-gray-500 w-24 shrink-0">
                  <Clock className="h-3 w-3" /> Comment:
                </span>
                <span className="truncate text-xs">
                  {customer.lastCommentTime ? formatTimeLabel(customer.lastCommentTime) : 'NA'}
                </span>
              </div>
            </div>
          </div>
        </div> */}

        {/* Chat */}
        {/* {customer.facebookConversationId && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3 text-center">
            <MessageCircle className="h-6 w-6 text-green-600 mx-auto mb-1" />
            <p className="text-xs text-green-700 mb-2">
              Active on Facebook Messenger
            </p>
            <Button
              size="sm"
              onClick={onChatMessage}
              className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs px-3"
            >
              <MessageCircle className="h-3.5 w-3.5 mr-1" />
              Chat Now
            </Button>
          </div>
        )} */}
      </CardContent>
    </Card>
  );
}
